import React, { Component } from 'react';
import {
    GECKO_CONSTS,
    GECKO_UTILS,
    GeckoFields,
    GeckoBtnsSorted,
    GeckoModalService,
    GeckoDialogService,
    GeckoToastService,
} from '@geckolabs/gecko-react-ui';
import { Get, Post } from '../../../../services/ApiRequest';
import Utils from '../../../../Utils';

const defaultUtcDateTime = () => new Date().toISOString().slice(0, 19).replace('T', ' ');

const INTEGRATION_TYPES = [
    'connect',
    'dotmailer',
    'ems',
    'newdynamics',
    'nottingham',
    'radius',
    'salesforce',
    'webhook',
    'engage-dynamics',
];

const TIME_FILTER_OPTIONS = [
    { value: 'created_since', label: 'Created since' },
    { value: 'not_updated_since', label: 'Not updated since' },
    { value: 'updated_since', label: 'Updated since' },
];

const SYNCABLE_TYPE_OPTIONS = [
    { value: '', label: 'All syncable types' },
    { value: String.raw`Gecko\Models\Attendance`, label: 'Attendance' },
    { value: String.raw`Gecko\Models\Call`, label: 'Call' },
    { value: String.raw`Gecko\Models\ChatConversation`, label: 'Chat conversation' },
    { value: String.raw`Gecko\Models\Contact`, label: 'Contact' },
    { value: String.raw`Gecko\Models\Event`, label: 'Event' },
    { value: String.raw`Gecko\Models\Message`, label: 'Message' },
    { value: String.raw`Gecko\Models\Note`, label: 'Note' },
    { value: String.raw`Gecko\Models\Response`, label: 'Response' },
    { value: String.raw`Gecko\Models\Subscriber`, label: 'Subscriber' },
];

const RELATED_TYPE_OPTIONS = [
    { value: '', label: 'Parent syncs only' },
    { value: 'Attendances', label: 'Attendances' },
    { value: 'consents', label: 'Consents' },
];

export const normalizeTimestampInput = (value) => {
    if (value == null || value === '') {
        return value;
    }

    const trimmed = String(value).trim();

    if (/^\d{10}$/.test(trimmed) || /^\d{13}$/.test(trimmed)) {
        return trimmed.length === 13
            ? String(Math.floor(Number(trimmed) / 1000))
            : trimmed;
    }

    return trimmed
        .replace(/Z$/i, '')
        .replace(/(\d{4}-\d{2}-\d{2})-?T/gi, '$1 ')
        .trim();
};

const parseSyncStatuses = (input) => {
    if (!input || !String(input).trim()) {
        return null;
    }
    const values = String(input)
        .split(',')
        .map((s) => Number.parseInt(s.trim(), 10))
        .filter((n) => !Number.isNaN(n));
    return values.length ? values : null;
};

const buildPayload = (accountId, state) => {
    const payload = {
        account_id: Number(accountId),
        integration_type: state.integration_type,
        time_filter: state.time_filter,
        since: normalizeTimestampInput(state.since),
        until: normalizeTimestampInput(state.until),
    };

    if (state.integration_id) {
        payload.integration_ids = [Number(state.integration_id)];
    }
    if (state.syncable_type) {
        payload.syncable_type = state.syncable_type;
    }
    if (state.related_type) {
        payload.related_type = state.related_type;
    }

    const syncStatuses = parseSyncStatuses(state.sync_statuses_input);
    if (syncStatuses) {
        payload.sync_statuses = syncStatuses;
    }
    if (state.release_locks) {
        payload.release_locks = true;
    }

    return payload;
};

const formatApiErrors = (errors) => {
    if (!errors?.length) {
        return 'Request failed';
    }
    return errors.map((e) => (typeof e === 'string' ? e : e.message || JSON.stringify(e))).join(', ');
};

const formatTimeSpan = (timeSpan) => {
    if (!timeSpan) {
        return null;
    }
    const { days, hours } = timeSpan;
    let text = `Spanning ${days} day${days === 1 ? '' : 's'}`;
    if (hours) {
        text += ` and ${hours} hour${hours === 1 ? '' : 's'}`;
    }
    return text;
};

const integrationTitle = (integrations, integrationId) => {
    const match = integrations.find((i) => i.id == integrationId);
    return match ? (match.name || match.title || match.type) : null;
};

export default class RequeueSyncs extends Component {

    constructor(props) {
        super();

        this.state = {
            account_id: props.account_id,
            integration_type: '',
            integration_id: '',
            time_filter: 'created_since',
            since: defaultUtcDateTime(),
            until: defaultUtcDateTime(),
            syncable_type: '',
            related_type: '',
            sync_statuses_input: '',
            release_locks: false,
            integrations: [],
            integrationsLoaded: false,
            integrationsError: null,
            preview: null,
        };
    }

    componentDidMount() {
        Get('/integrations/for-account', { account_id: this.props.account_id })
            .then((response) => {
                this.setState({
                    integrations: response.data || [],
                    integrationsLoaded: true,
                });
            })
            .catch((err) => {
                this.setState({
                    integrationsLoaded: true,
                    integrationsError: GECKO_UTILS.renderError(err),
                });
                new GeckoToastService().error('Failed to load integrations');
            });
    }

    integrationsForType(integrationType) {
        if (!integrationType) {
            return this.state.integrations;
        }
        return this.state.integrations.filter((i) => i.type === integrationType);
    }

    integrationTypeOptions() {
        const onAccount = [...new Set(this.state.integrations.map((i) => i.type).filter(Boolean))];
        const types = onAccount.length
            ? INTEGRATION_TYPES.filter((t) => onAccount.includes(t))
            : INTEGRATION_TYPES;

        return types.map((type) => ({ value: type, label: type }));
    }

    canPreview() {
        if (!this.state.integrationsLoaded || this.state.integrationsError) {
            return false;
        }
        return Boolean(
            this.state.integration_type
            && this.state.since
            && this.state.until
        );
    }

    getFields() {
        const integrationOptions = [
            { value: '', label: 'All integrations of this type' },
            ...this.integrationsForType(this.state.integration_type).map((i) => ({
                value: i.id,
                label: i.name ? `${i.name} (${i.type})` : i.type,
            })),
        ];

        return [
            {
                key: 'integration_type',
                label: 'Integration type',
                type: GECKO_CONSTS.FIELD_TYPE_SELECT,
                width: '100%',
                required: true,
                optionValueKey: 'value',
                optionsLabelKey: 'label',
                options: this.integrationTypeOptions(),
                config: { clearable: false },
            },
            {
                key: 'integration_id',
                label: 'Integration',
                type: GECKO_CONSTS.FIELD_TYPE_SELECT,
                width: '100%',
                optionValueKey: 'value',
                optionsLabelKey: 'label',
                options: integrationOptions,
                config: { clearable: true },
                disabledWhen: () => !this.state.integration_type,
            },
            {
                key: 'time_filter',
                label: 'Time filter',
                type: GECKO_CONSTS.FIELD_TYPE_SELECT,
                width: '100%',
                required: true,
                optionValueKey: 'value',
                optionsLabelKey: 'label',
                options: TIME_FILTER_OPTIONS,
                config: { clearable: false },
            },
            {
                key: 'since',
                label: 'Since (UTC datetime or unix seconds)',
                type: GECKO_CONSTS.FIELD_TYPE_TEXT,
                width: '100%',
                required: true,
            },
            {
                key: 'until',
                label: 'Until (UTC datetime or unix seconds)',
                type: GECKO_CONSTS.FIELD_TYPE_TEXT,
                width: '100%',
                required: true,
            },
            {
                key: 'syncable_type',
                label: 'Syncable type',
                type: GECKO_CONSTS.FIELD_TYPE_SELECT,
                width: '100%',
                optionValueKey: 'value',
                optionsLabelKey: 'label',
                options: SYNCABLE_TYPE_OPTIONS,
                config: { clearable: true },
            },
            {
                key: 'related_type',
                label: 'Related type',
                type: GECKO_CONSTS.FIELD_TYPE_SELECT,
                width: '100%',
                optionValueKey: 'value',
                optionsLabelKey: 'label',
                options: RELATED_TYPE_OPTIONS,
                config: { clearable: true },
            },
            {
                key: 'sync_statuses_input',
                label: 'Sync statuses (optional, comma-separated)',
                type: GECKO_CONSTS.FIELD_TYPE_TEXT,
                width: '100%',
            },
            {
                key: 'release_locks',
                label: 'Release syncing locks before requeue',
                type: GECKO_CONSTS.FIELD_TYPE_TOGGLE,
                width: '100%',
            },
        ];
    }

    handleChange(key, val) {
        const next = { [key]: val, preview: null };

        if (key === 'integration_id' && val) {
            const integration = this.state.integrations.find((i) => i.id == val);
            if (integration?.type) {
                next.integration_type = integration.type;
            }
        }

        if (key === 'integration_type') {
            const current = this.state.integrations.find((i) => i.id == this.state.integration_id);
            if (current && current.type !== val) {
                next.integration_id = '';
            }
        }

        this.setState(next);
    }

    previewSyncs() {
        const payload = buildPayload(this.props.account_id, this.state);

        return Post('/admin/requeue-syncs/preview', payload)
            .then((response) => {
                if (!response.success) {
                    new GeckoToastService().error(formatApiErrors(response.errors));
                    return;
                }
                this.setState({ preview: response.data });
            })
            .catch((err) => {
                new GeckoToastService().error(GECKO_UTILS.renderError(err));
            });
    }

    executeSyncs() {
        const payload = buildPayload(this.props.account_id, this.state);
        const total = this.state.preview ? this.state.preview.total : 0;
        const message = (
            <div>
                <p>
                    Requeue <strong>{Utils.numberWithCommas(total)}</strong> sync{total === 1 ? '' : 's'} for this account?
                </p>
                <p className="mb-0">These syncs cannot be stopped.</p>
                {this.state.release_locks && (
                    <p className="mb-0">Syncing locks will be released before requeueing.</p>
                )}
                {total > 10000 && (
                    <p className="mb-0">{Utils.numberWithCommas(total)}?! You better be really fucking sure about this.</p>
                )}
            </div>
        );

        return new GeckoDialogService().confirm(message).then((ok) => {
            if (!ok) {
                return;
            }
            return Post('/admin/requeue-syncs', payload)
                .then((response) => {
                    if (!response.success) {
                        new GeckoToastService().error(formatApiErrors(response.errors));
                        return;
                    }
                    new GeckoToastService().success('Syncs requeued successfully');
                    new GeckoModalService().close();
                })
                .catch((err) => {
                    new GeckoToastService().error(GECKO_UTILS.renderError(err));
                });
        });
    }

    renderPreviewSummary() {
        const { preview, integrations } = this.state;
        if (!preview) {
            return null;
        }

        const total = Utils.numberWithCommas(preview.total);
        const timeSpanText = formatTimeSpan(preview.time_span);

        return (
            <div className="col-12">
                <div className="alert alert-info">
                    <p className="mb-1">
                        <span className="font-weight-bold">{total}</span> sync{preview.total === 1 ? '' : 's'} would be requeued.
                    </p>
                    {timeSpanText && <p className="mb-1">{timeSpanText} (since → until)</p>}
                    {preview.by_integration && preview.by_integration.length > 0 && (
                        <ul className="mb-0 pl-3">
                            {preview.by_integration.map((row) => {
                                const title = integrationTitle(integrations, row.integration_id);
                                const label = title
                                    ? `${title}: ${Utils.numberWithCommas(row.count)}`
                                    : `Integration ${row.integration_id}: ${Utils.numberWithCommas(row.count)}`;
                                return <li key={row.integration_id}>{label}</li>;
                            })}
                        </ul>
                    )}
                </div>
            </div>
        );
    }

    get modalButtons() {
        return [
            {
                name: 'Cancel',
                preset: GECKO_CONSTS.BTN_PRESET_CLOSE,
                handler: () => new GeckoModalService().close(),
            },
            {
                name: 'Preview',
                preset: GECKO_CONSTS.BTN_PRESET_SAVE,
                disabledWhen: () => !this.canPreview(),
                handler: () => this.previewSyncs(),
            },
            {
                name: 'Requeue syncs',
                preset: GECKO_CONSTS.BTN_PRESET_SAVE,
                disabledWhen: () => !this.state.preview,
                handler: () => this.executeSyncs(),
            },
        ];
    }

    render() {
        const { integrationsLoaded, integrationsError } = this.state;

        return (
            <div>
                {integrationsError && (
                    <div className="col-12">
                        <div className="alert alert-warning">{integrationsError}</div>
                    </div>
                )}
                {!integrationsLoaded && !integrationsError && (
                    <div className="col-12">
                        <div className="alert alert-info">Loading integrations…</div>
                    </div>
                )}
                {integrationsLoaded && !integrationsError && (
                    <GeckoFields
                        fields={this.getFields()}
                        values={this.state}
                        handler={this.handleChange.bind(this)}
                    />
                )}
                {this.renderPreviewSummary()}
                <div className="modal-footer">
                    <GeckoBtnsSorted items={this.modalButtons} />
                </div>
            </div>
        );
    }
}
